import { AutoComplete, ISelectType, useFetch, useFormContext } from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import MainApi from "../../../../../../services/mainApi.service";
import TicketServService from "../../../../../../services/ticket.service";
import TicketServAreaService from "../../../../../../services/ticketServiceArea.service";
import { TTicketAutoTransferRuleForm } from "./TicketAutoTransferRuleSchema";

const TICKET_TYPE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const ticketTypeService = new MainApi(TICKET_TYPE_ROUTE);
const ticketService = new TicketServService();
const ticketServiceArea = new TicketServAreaService();

const translationPath = "page.settings.crm.ticketAutoTransferRules.";

const TicketServiceFields = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TTicketAutoTransferRuleForm>();
  const [serviceAreaOptions, setServiceAreaOptions] = useState<ISelectType[]>([]);
  const [hourAtendOptions, setHourAtendOptions] = useState<ISelectType[]>([]);
  const ticketServiceSelected = useWatch({ name: "ticketService", control });
  const previousTicketServiceId = useRef<string | null>(null);
  const hourAtendLoaded = useRef(false);
  const { sendRequest: sendTicketServiceAreaRequest } = useFetch(ticketServiceArea.list);
  const { sendRequest: sendHourAtendRequest } = useFetch(ticketTypeService.list);

  useEffect(() => {
    const currentTicketServiceId = ticketServiceSelected?.id ?? null;
    const previousId = previousTicketServiceId.current;

    if (previousId === currentTicketServiceId) {
      return;
    }

    previousTicketServiceId.current = currentTicketServiceId;

    if (!currentTicketServiceId) {
      setValue("ticketServiceAreas", null);
      setValue("ticketAutoTransferRuleHourAtend", null);
      setServiceAreaOptions([]);
      return;
    }

    // Preserve loaded values on first edit load; clear only when user changes service.
    if (previousId !== null) {
      setValue("ticketServiceAreas", null);
      setValue("ticketAutoTransferRuleHourAtend", null);
    }

    const loadServiceAreas = async () => {
      const { data, success } = await sendTicketServiceAreaRequest({
        TicketServiceId: currentTicketServiceId,
      });
      if (data && success) {
        setServiceAreaOptions(data);
      }
    };
    loadServiceAreas();
  }, [sendTicketServiceAreaRequest, setValue, ticketServiceSelected?.id]);

  useEffect(() => {
    if (!ticketServiceSelected?.id || hourAtendLoaded.current) {
      return;
    }

    const loadHourAtendOptions = async () => {
      const { data, success } = await sendHourAtendRequest({
        identifier: "TicketAutoTransferRuleHourAtend",
      });
      if (data && success) {
        setHourAtendOptions(data);
        hourAtendLoaded.current = true;
      }
    };

    loadHourAtendOptions();
  }, [sendHourAtendRequest, ticketServiceSelected?.id]);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <AutoComplete
          getOptionLabel={(option) => option.name}
          size="small"
          label={t(translationPath + "ticket_service")}
          name="ticketService"
          request={ticketService.ticketServiceList}
        />
      </Grid>

      {ticketServiceSelected && (
        <>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={(option) => option.name}
              size="small"
              label={t(translationPath + "ticket_service_area")}
              name="ticketServiceAreas"
              options={serviceAreaOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={(option) => option.name}
              size="small"
              label={t(translationPath + "ticket_auto_transfer_rule_hour_atend")}
              name="ticketAutoTransferRuleHourAtend"
              options={hourAtendOptions}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default TicketServiceFields;
